import { DefaultTheme as StyledComponentDefaultTheme } from 'styled-components';
import {
  BorderRadius,
  Brand,
  Gradient,
  LinkButton,
  ListSizes,
  Shadow,
  TextSizes,
} from './theme.types';
import {
  Color,
  FontSize,
  LineHeight,
  FontWeight,
  Spacing,
  Typography,
  ContainerWidth,
} from '../types';
import { getLinearGradient } from '../utils';
import { Breakpoints } from '../breakpoint';
import { doneDealTheme } from './donedeal.theme';

const PRIMARY_INTENTS: Color = {
  lighter: '#F6EFFF',
  light: '#C799FF',
  base: '#953FFF',
  dark: '#7732CC',
  darker: '#3C1966',
  textContrast: '#fff',
};

const SECONDARY_INTENTS: Color = {
  lighter: '#F2F2F2',
  light: '#D9D9D9',
  base: '#999999',
  dark: '#666666',
  darker: '#333333',
  textContrast: '#fff',
};

const TERTIARY_INTENTS: Color = {
  lighter: '#D4ECD4',
  light: '#7ACC7C',
  base: '#3EBA40',
  dark: '#308A32',
  darker: '#215C22',
  textContrast: '#fff',
};

const GREEN: Color = {
  base: '#3EBA40',
  light: '#7ACC7C',
  lighter: '#D4ECD4',
  dark: '#308A32',
  darker: '#215C22',
  textContrast: '#fff',
};
const ORANGE: Color = {
  base: '#FF9700',
  light: '#FFCB80',
  lighter: '#FFEACC',
  dark: '#CC7900',
  darker: '#663C00',
  textContrast: '#fff',
};
const RED: Color = {
  base: '#CC2324',
  light: '#FFCB80',
  lighter: '#FFEACC',
  dark: '#CC7900',
  darker: '#663C00',
  textContrast: '#fff',
};
const GREY: Color = {
  base: '#999999',
  light: '#D9D9D9',
  lighter: '#F2F2F2',
  dark: '#666666',
  darker: '#333333',
  textContrast: '#fff',
};

const FONT_SIZE: FontSize = {
  S10: '10px',
  S12: '12px',
  M14: '14px',
  M16: '16px',
  L18: '18px',
  L20: '20px',
  L24: '24px',
  B10: '10px',
  B12: '12px',
  B14: '14px',
  B16: '16px',
  B18: '18px',
  H24: '24px',
  H32: '32px',
  H40: '40px',
};

const LINE_HEIGHT: LineHeight = {
  S16: '16px',
  M24: '24px',
  L32: '32px',
  XL48: '48px',
};

const TEXT_SIZES: TextSizes = {
  '2xs': [FONT_SIZE.S10, LINE_HEIGHT.S16],
  xs: [FONT_SIZE.S12, LINE_HEIGHT.S16],
  s: [FONT_SIZE.M14, LINE_HEIGHT.S16],
  m: [FONT_SIZE.M16, LINE_HEIGHT.M24],
  l: [FONT_SIZE.L18, LINE_HEIGHT.L32],
  xl: [FONT_SIZE.L24, LINE_HEIGHT.L32],
  '2xl': [FONT_SIZE.H32, LINE_HEIGHT.XL48],
  '3xl': [FONT_SIZE.H40, LINE_HEIGHT.XL48],
};
const META_LIST_SIZES: ListSizes = {
  s: [FONT_SIZE.S12, LINE_HEIGHT.S16],
  m: [FONT_SIZE.M14, LINE_HEIGHT.M24],
};

const FONT_WEIGHT: FontWeight = {
  regular: 400,
  bold: 700,
};

const SPACING: Spacing = {
  S0: '0px',
  S2: '2px',
  S4: '4px',
  S8: '8px',
  S12: '12px',
  M16: '16px',
  M24: '24px',
  M32: '32px',
  L40: '40px',
  L48: '48px',
  L56: '56px',
  XL64: '64px',
  XL72: '72px',
  XL80: '80px',
};

const brand: Brand = {
  primary: PRIMARY_INTENTS,
  secondary: SECONDARY_INTENTS,
  tertiary: TERTIARY_INTENTS,
  error: RED,
  warning: ORANGE,
  info: PRIMARY_INTENTS,
  success: GREEN,
  background: {
    // TODO: Should this be base, dark, light instead?
    primary: GREY.textContrast,
    secondary: GREY.lighter,
    disabled: GREY.light,
    hover: GREY.lighter,
    selected: GREY.lighter,
  },
  text: {
    primary: GREY.dark,
    secondary: GREY.dark,
    disabled: GREY.dark,
  },
  action: {
    active: GREY.lighter,
    hover: GREY.dark,
    selected: GREY.dark,
    disabled: GREY.dark,
    focus: GREY.dark,
  },
};

const base: {
  brand: Brand;
  spacing: Spacing;
  typography: Typography;
  borderRadius: BorderRadius;
  shadow: Shadow;
  gradient: Gradient;
  breakpoints: Breakpoints<string>;
  containerWidth: ContainerWidth<string>;
} = {
  brand,
  spacing: SPACING,
  typography: {
    fontSizes: FONT_SIZE,
    fontWeights: FONT_WEIGHT,
    lineHeights: LINE_HEIGHT,
    letterSpacings: SPACING,
  },
  borderRadius: {
    s: '4px',
    m: '8px',

    // These are DD-only border radii.
    default: '4px',
    medium: '6px',
    large: '8px',
  },
  shadow: {
    primary: '0 1px 4px  rgba(51, 51, 51, 0.24)',
    secondary: '0 -1px 4px rgba(51, 51, 51, 0.24)',

    // These are DD-only shadows.
    CONTAINER: '0 1px 4px 0 rgba(88, 97, 118, 0.16)',
    CONTAINER_INVERT: '0 -1px 4px 0 rgba(88, 97, 118, 0.12)',
    LARGE: '0 2px 8px 0 rgba(88, 97, 118, 0.12)',
    XLARGE: '0px 2px 8px rgba(96, 134, 230, 0.24)',
  },
  gradient: {
    primary: 'linear-gradient(180deg, #953FFF 0%, #7732CC 98.93%)',
    secondary: 'linear-gradient(180deg, #3EBA40 0%, #308A32 100%)',
  },
  breakpoints: {
    xs: '0', //phone
    sm: '600', // tablet
    md: '1200', // small laptop
    lg: '1536', // large screen
  },
  containerWidth: {
    xs: '360',
    s: '792',
    m: '1320',
  },
};

const text = {
  base: {
    color: brand.text.primary,
  },
  sizes: TEXT_SIZES,
  lineHeightSizes: {
    s: LINE_HEIGHT.S16,
    m: LINE_HEIGHT.M24,
    l: LINE_HEIGHT.L32,
    xl: LINE_HEIGHT.XL48,
  },
};
const button = {
  // determines the height of the button
  sizes: {
    s: '32px',
    m: '40px',
    l: '48px',
  },
  base: {
    borderRadius: '4px',
    fontSize: '16px',
    lineHeight: '24px',
    padding: '8px',
    boxShadow: base.shadow.primary,
    ':disabled': {
      boxShadow: 'none',
    },
  },
  intent: {
    primary: {
      background: base.gradient.primary,
      color: brand.primary.textContrast,
      fontWeight: base.typography.fontWeights.bold,
      ':hover': {
        background: getLinearGradient(brand.primary.light, brand.primary.dark),
      },
      ':active': {
        background: brand.primary.dark,
      },
      ':focus-visible': {
        background: getLinearGradient(brand.primary.base, brand.primary.dark),
        outlineColor: brand.primary.darker,
      },
      ':disabled': {
        background: brand.background.disabled,
      },
    },
    secondary: {
      background: '#FFFFFF',
      border: `1px solid ${brand.secondary.base}`,
      color: brand.secondary.dark,
      fontWeight: base.typography.fontWeights.bold,

      ':hover': {
        border: `1px solid ${brand.primary.base}`,
        color: brand.primary.base,
      },
      ':active': {
        border: `1px solid ${brand.primary.dark}`,
        color: brand.primary.dark,
      },
      ':focus-visible': {
        border: `2px solid ${brand.secondary.dark}`,
        color: brand.secondary.dark,
        outline: '0',
      },
      ':disabled': {
        background: brand.background.disabled,
        color: '#FFFFFF',
        border: 'none',
      },
    },
  },
};

const linkButton: LinkButton = {
  base: {
    // fontSize: '14px',
    // lineHeight: '24px',
  },
  intent: {
    primary: {
      background: 'transparent',
      textTransform: 'uppercase',
      color: brand.primary.base,
      fontWeight: base.typography.fontWeights.bold,
      ':hover': {
        color: brand.primary.dark,
      },
      ':active': {
        color: brand.primary.dark,
      },
      ':focus-visible': {
        textDecoration: 'underline',
        color: brand.primary.base,
      },
      ':disabled': {
        color: brand.secondary.base,
      },
    },
    secondary: {
      background: 'transparent',
      color: brand.primary.base,
      fontWeight: base.typography.fontWeights.regular,
      ':hover': {
        color: brand.primary.dark,
      },
      ':active': {
        color: brand.primary.dark,
      },
      ':focus-visible': {
        textDecoration: 'underline',
        color: brand.primary.base,
      },
      ':disabled': {
        color: brand.secondary.base,
      },
    },
  },
};

const icon = {
  sizes: {
    '2xs': '8px',
    xs: '12px',
    s: '16px',
    m: '24px',
    l: '32px',
    xl: '40px',
    '2xl': '48px',
    '3xl': '56px',
    '4xl': '64px',
  },
};

const dialog = {
  sizes: {
    s: '320px',
    m: '440px',
    l: '640px',
  },
};

const panel = {
  base: {
    background: brand.background.primary,
    borderRadius: base.borderRadius.s,
    boxShadow: base.shadow.primary,
  },
};

const textarea = {
  base: {
    color: GREY.darker,
    borderColor: GREY.base,
    background: brand.background.primary,
    borderRadius: base.borderRadius.s,
    lineHeight: '24px',
  },
};

const list = {
  base: {
    color: GREY.darker,
  },
  sizes: META_LIST_SIZES,
};

export const defaultTheme: StyledComponentDefaultTheme = {
  ...base,
  components: {
    text,
    button,
    linkButton,
    icon,
    panel,
    textarea,
    list,
    dialog,
  },

  // This is the object we want DD to use (for now). Ideally components in apps/donedeal would use
  // the normal styled-system theme properties above. For now, we're in the middle of migrating
  // them. This solution allows a piecewise migration:
  // 1. Move all components in donedeal to reference the new theme.donedeal.* properties.
  // 2. Remove the 'piecewise migration support' properties below.
  // 3. Consolidate duplicated components within DoneDeal/toolkits and libs/toolkit.
  // 4. Work with design to convert DoneDeal-specific components to use the theme properties above.
  donedeal: doneDealTheme,

  // START: piecewise migration support.
  // to support a piecewise migration, we'll create some getters here which backfill the old
  // theme object. This allows existing code to continue to function while we migrate everything
  // in apps/donedeal to use the new theme.donedeal.* properties.
  colors: doneDealTheme.colors,
  fontSize: doneDealTheme.fontSize,
  fontWeight: doneDealTheme.fontWeight,
  overlay: doneDealTheme.overlay,
  zIndices: doneDealTheme.zIndices,
  getOuterShadow: doneDealTheme.getOuterShadow,
  // END: piecewise migration support.
};
